import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import CompanyService from "../../../../app/data/company/companyService";
import { SearchRequesterModel, SearchRequestersResponseModel } from "../../../../app/data/company/models";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import PickupRequestsService from "../../../../app/data/pickup-requests/pickupRequestsService";
import LabeledSelectInput from "../../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import Loading from "../../../../ui-components/loading/loading";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import InviteUser from "../../../invite-user/inviteUser";
import {
  getEffectiveSenderEmail,
  isEmailRegisteredOnTeam,
  resolveRequesterTeamFromSenderEmail,
  resolveRequesterTeamName,
} from "../utils/requesterCompanyUtils";
import { isXgsiEmailDomain } from "../utils/bolShipperEmailUtils";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "../utils/pickupInboxQueryOptions";

const companyService = CompanyService.getInstance();
const pickupRequestsService = PickupRequestsService.getInstance();

const requesterOption = (company: SearchRequesterModel): XGSSelectOption => ({
  label: company.name,
  subtitle: company.accountName ? `${company.accountName} (${company.accountNumber})` : undefined,
  value: company.id,
});

const getRequesterSearchOptions = (search: SearchRequestersResponseModel | null) => {
  if (!search) return [];
  return [
    {
      label: "By Company:",
      options: search.teams.map(requesterOption),
    },
    {
      label: "By Account:",
      options: search.accounts.map(requesterOption),
    },
  ];
};

export interface RequesterCompanyFieldProps {
  pickupRequestId: number;
  senderEmail?: string;
  companyNameHint?: string;
  readOnly: boolean;
  showValidationErrors?: boolean;
  preferExplicitTeamSelection?: boolean;
  onReadyChange: (ready: boolean) => void;
  onTeamChange: (teamId: string | null, teamName?: string | null) => void;
}

const RequesterCompanyField: React.FC<RequesterCompanyFieldProps> = ({
  pickupRequestId,
  senderEmail,
  companyNameHint,
  readOnly,
  showValidationErrors,
  preferExplicitTeamSelection = false,
  onReadyChange,
  onTeamChange,
}) => {
  const queryClient = useQueryClient();
  const [selectedTeam, setSelectedTeam] = useState<XGSSelectOption | null>(null);
  const [resolvedTeamName, setResolvedTeamName] = useState<string | null>(null);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [isSearchingTeam, setIsSearchingTeam] = useState(false);
  const [senderInviteSent, setSenderInviteSent] = useState(false);
  const [userDismissedEmailTeamResolve, setUserDismissedEmailTeamResolve] = useState(false);

  const { data: pickupRequest, isLoading: isPickupLoading } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST, pickupRequestId],
    queryFn: () => pickupRequestsService.getPickupRequest(pickupRequestId),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const effectiveSenderEmail = useMemo(
    () =>
      getEffectiveSenderEmail(
        pickupRequest?.senderEmail,
        pickupRequest?.forwardedSenderEmail,
        senderEmail
      ),
    [pickupRequest?.senderEmail, pickupRequest?.forwardedSenderEmail, senderEmail]
  );

  const customerSenderEmail = useMemo(() => {
    const email = effectiveSenderEmail?.toLowerCase();
    if (!email || isXgsiEmailDomain(email)) return undefined;
    return email;
  }, [effectiveSenderEmail]);

  const isInternalSender = useMemo(() => {
    const email = effectiveSenderEmail?.toLowerCase();
    return !!email && isXgsiEmailDomain(email);
  }, [effectiveSenderEmail]);

  const { data: emailResolvedTeam, isFetching: isResolvingTeamFromEmail } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_REQUESTER_TEAM_BY_EMAIL, pickupRequestId, customerSenderEmail],
    queryFn: () => resolveRequesterTeamFromSenderEmail(customerSenderEmail),
    enabled:
      !readOnly &&
      !preferExplicitTeamSelection &&
      !isPickupLoading &&
      !!pickupRequest &&
      !pickupRequest.senderTeamId &&
      !selectedTeam?.value &&
      !!customerSenderEmail &&
      !userDismissedEmailTeamResolve,
    retry: false,
    staleTime: Infinity,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const resolvedTeamId = pickupRequest?.senderTeamId || (selectedTeam?.value as string | undefined) || null;

  const { data: fetchedTeamName, isLoading: isResolvingTeamName } = useQuery({
    queryKey: [
      QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST,
      pickupRequestId,
      "resolved-team-name",
      pickupRequest?.senderTeamId,
    ],
    queryFn: () => resolveRequesterTeamName(pickupRequest!.senderTeamId!),
    enabled: !!pickupRequest?.senderTeamId,
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const teamName = selectedTeam?.label || resolvedTeamName || fetchedTeamName;
  const needsTeamSelection = !readOnly && !pickupRequest?.senderTeamId;

  useEffect(() => {
    if (!pickupRequest?.senderTeamId) return;
    onTeamChange(pickupRequest.senderTeamId, fetchedTeamName ?? null);
  }, [fetchedTeamName, onTeamChange, pickupRequest?.senderTeamId]);

  useEffect(() => {
    setSelectedTeam(null);
    setResolvedTeamName(null);
    setSenderInviteSent(false);
    setUserDismissedEmailTeamResolve(false);
  }, [pickupRequestId]);

  useEffect(() => {
    if (!emailResolvedTeam || selectedTeam?.value) return;

    const option = requesterOption({
      id: emailResolvedTeam.teamId,
      name: emailResolvedTeam.teamName,
    });
    setSelectedTeam(option);
    setResolvedTeamName(emailResolvedTeam.teamName);
    onTeamChange(emailResolvedTeam.teamId, emailResolvedTeam.teamName);
  }, [emailResolvedTeam, onTeamChange, selectedTeam?.value]);

  const { data: senderRegisteredOnTeam, isLoading: isCheckingSender } = useQuery({
    queryKey: [
      QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST,
      pickupRequestId,
      "sender-on-team",
      resolvedTeamId,
      effectiveSenderEmail,
    ],
    queryFn: () => isEmailRegisteredOnTeam(resolvedTeamId!, effectiveSenderEmail!),
    enabled: !!resolvedTeamId && !!effectiveSenderEmail && !isInternalSender,
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const senderIsRegistered = useMemo(() => {
    if (senderInviteSent) return true;
    if (!resolvedTeamId || !effectiveSenderEmail) return false;
    if (pickupRequest?.senderTeamId && pickupRequest.isSenderRegistered) return true;
    return senderRegisteredOnTeam === true;
  }, [
    effectiveSenderEmail,
    pickupRequest?.isSenderRegistered,
    pickupRequest?.senderTeamId,
    resolvedTeamId,
    senderInviteSent,
    senderRegisteredOnTeam,
  ]);

  const needsSenderInvite =
    !readOnly && !!resolvedTeamId && !!effectiveSenderEmail && !senderIsRegistered && !isInternalSender;

  const requesterReady =
    !!resolvedTeamId && (!effectiveSenderEmail || senderIsRegistered || isInternalSender);

  useEffect(() => {
    onReadyChange(requesterReady);
  }, [onReadyChange, requesterReady]);

  useEffect(() => {
    if (selectedTeam?.value) {
      onTeamChange(String(selectedTeam.value), selectedTeam.label);
    } else if (!pickupRequest?.senderTeamId) {
      onTeamChange(null, null);
    }
  }, [onTeamChange, pickupRequest?.senderTeamId, selectedTeam]);

  const searchRequester = useMemo(
    () =>
      debounce((query: string, callback: (options: ReturnType<typeof getRequesterSearchOptions>) => void) => {
        setIsSearchingTeam(true);
        companyService
          .getRequestersGlobalSearch(query)
          .then((options) => callback(getRequesterSearchOptions(options)))
          .finally(() => setIsSearchingTeam(false));
      }, 300),
    []
  );

  useEffect(() => () => searchRequester.cancel(), [searchRequester]);

  const onChangeTeam = (option: XGSSelectOption | null | undefined) => {
    setSelectedTeam(option ?? null);
    setSenderInviteSent(false);
    if (option?.value) {
      setResolvedTeamName(option.label);
      setUserDismissedEmailTeamResolve(false);
    } else {
      setResolvedTeamName(null);
      setUserDismissedEmailTeamResolve(true);
    }
  };

  const onClearSelectedTeam = () => {
    setSelectedTeam(null);
    setResolvedTeamName(null);
    setSenderInviteSent(false);
    setUserDismissedEmailTeamResolve(true);
    onTeamChange(null, null);
  };

  const onInviteSent = () => {
    setSenderInviteSent(true);
    void queryClient.refetchQueries({
      queryKey: [
        QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST,
        pickupRequestId,
        "sender-on-team",
      ],
    });
  };

  if (isPickupLoading) {
    return (
      <div className="xgs-captured-bol__requester-company">
        <Loading isLoading />
      </div>
    );
  }

  return (
    <div
      className={[
        "xgs-captured-bol__requester-company",
        showValidationErrors && !requesterReady ? "xgs-captured-bol__requester-company--error" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="xgs-bol-field xgs-bol-field--figma xgs-bol-field--compact">
        <div className="xgs-bol-field__label-row">
          <span className="xgs-bol-field__label xgs-bol-field__label--figma">
            Requester Company
            <span className="xgs-bol-field__required"> *</span>
          </span>
        </div>

        {needsTeamSelection ? (
          selectedTeam || (!preferExplicitTeamSelection && isResolvingTeamFromEmail) ? (
            <div className="xgs-captured-bol__requester-company__resolved">
              <div className="xgs-bol-field__figma-readonly">
                {!preferExplicitTeamSelection && isResolvingTeamFromEmail
                  ? "Looking up customer team..."
                  : selectedTeam?.label || "—"}
              </div>
              {!readOnly && selectedTeam && (
                <button
                  type="button"
                  className="xgs-captured-bol__requester-company__change"
                  onClick={onClearSelectedTeam}
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            <LabeledSelectInput
              isDisabled={readOnly}
              isLoading={isSearchingTeam}
              label=""
              labelMode={LabelModes.column}
              onValueChange={onChangeTeam}
              options={[]}
              required
              placeholder="Enter company name..."
              value={selectedTeam}
              isClearable
              async
              cacheOptions={false}
              loadOptions={searchRequester}
              className="xgs-captured-bol__requester-select"
              isOptionSelected={(option, selectValue) =>
                option.value === selectValue[0]?.value && option.label === selectValue[0]?.label
              }
            />
          )
        ) : (
          <div className="xgs-bol-field__figma-readonly">
            {teamName || (isResolvingTeamName ? <Loading isLoading /> : "—")}
          </div>
        )}
      </div>

      {needsSenderInvite && teamName && effectiveSenderEmail && (
        <div className="xgs-captured-bol__requester-invite-banner">
          <p className="xgs-captured-bol__requester-invite-text">
            Add the sender&apos;s email address ({effectiveSenderEmail}) as a user account for the
            customer ({teamName}).
          </p>
          <Button
            theme={ButtonThemes.blue}
            type="button"
            className="xgs-captured-bol__requester-invite-btn"
            onClick={() => setShowInviteUser(true)}
            disabled={isCheckingSender}
          >
            Add user to customer team
          </Button>
        </div>
      )}

      {!readOnly && resolvedTeamId && !effectiveSenderEmail && (
        <div className="xgs-captured-bol__requester-invite-banner xgs-captured-bol__requester-invite-banner--warn">
          Sender email is not available on this pickup request. Confirm the requester company before
          creating the pickup.
        </div>
      )}

      <InviteUser
        show={showInviteUser}
        teamId={resolvedTeamId ?? undefined}
        initialEmail={effectiveSenderEmail}
        onInviteSent={onInviteSent}
        onClose={() => setShowInviteUser(false)}
      />
    </div>
  );
};

export default RequesterCompanyField;
